import * as React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { MessageBar, MessageBarType, Spinner, SpinnerSize } from '@fluentui/react';
import { Context } from '@micro-frontend-react/core/lib/Context';
import type { Context as ReactContext } from 'react';
import type { IEmployeeExperienceContext } from '@micro-frontend-react/employee-experience/lib/IEmployeeExperienceContext';
import { useDynamicReducer } from '@micro-frontend-react/employee-experience/lib/useDynamicReducer';

import type { IPBTCase } from '../../../Interfaces/IPBTCase';
import { CaseDetail } from '../../../Components/PBT/CaseDetail/CaseDetail';
import { PBTApiEndpoints } from '../../../Services/PBTConstants';
import { mapApiCaseToUiCase } from '../HRCDashboard/useHRCDashboard';
import { pbtCasesReducerName, pbtCasesReducer } from '../../../Redux/PBT/Cases/reducers';
import { requestCasesSagas } from '../../../Services/PBTCaseService';

const CaseDetailPage: React.FC = () => {
  const { authClient } = React.useContext(Context as ReactContext<IEmployeeExperienceContext>);
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const caseId = Number(id);
  const [caseData, setCaseData] = React.useState<IPBTCase | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  useDynamicReducer(pbtCasesReducerName, pbtCasesReducer, [requestCasesSagas]);

  const fetchCaseDetail = React.useCallback(async () => {
    if (!Number.isInteger(caseId) || caseId <= 0) {
      setCaseData(null);
      setError('Invalid case ID.');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const token = await authClient.acquireToken(__SCOPES__);
      const response = await fetch(PBTApiEndpoints.CaseDetail(caseId), {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error(`API returned ${response.status}`);
      }

      const json = await response.json();
      const apiCase = json?.data ?? json;
      setCaseData(mapApiCaseToUiCase(apiCase as Record<string, unknown>));
    } catch {
      setCaseData(null);
      setError('Unable to load the case. Return to the dashboard and try again.');
    } finally {
      setIsLoading(false);
    }
  }, [authClient, caseId]);

  React.useEffect(() => {
    fetchCaseDetail();
  }, [fetchCaseDetail]);

  if (isLoading) {
    return <Spinner size={SpinnerSize.large} label="Loading case details..." />;
  }

  if (!caseData) {
    return (
      <MessageBar messageBarType={MessageBarType.error}>
        {error ?? 'Case not found.'}
      </MessageBar>
    );
  }

  return (
    <CaseDetail
      selectedCase={caseData}
      onClose={() => history.push('/PBT/Dashboard')}
      onCaseUpdated={fetchCaseDetail}
      onCaseReleased={fetchCaseDetail}
    />
  );
};

export default CaseDetailPage;
